import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';

interface NotificationForm {
  title: string;
  body: string;
  url?: string;
  userIds: string[];
  sendToAll: boolean;
}

interface User {
  id: number;
  name: string;
  email: string;
  is_active: boolean;
}

const PushNotificationsPage: React.FC = () => {
  const [form, setForm] = useState<NotificationForm>({
    title: '',
    body: '',
    url: '',
    userIds: [],
    sendToAll: true
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Carregar lista de usuários
  useEffect(() => {
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await api.get('/admin/users?limit=100');
        if (response.data.success) {
          setUsers(response.data.data.users);
        }
      } catch (error) {
        console.error('Erro ao carregar usuários:', error);
      } finally {
        setLoadingUsers(false);
      }
    };

    loadUsers();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const payload = {
        title: form.title,
        body: form.body,
        url: form.url || '/',
        actions: []
      };

      if (form.sendToAll) {
        // Send to all users
        await api.post('/push-notifications/send-broadcast', payload);
      } else if (form.userIds.length === 1) {
        // Send to single user
        await api.post(`/push-notifications/send-notification/${form.userIds[0]}`, payload);
      } else if (form.userIds.length > 1) {
        // Send to multiple users
        const results = [];
        for (const userId of form.userIds) {
          try {
            await api.post(`/push-notifications/send-notification/${userId}`, payload);
            results.push({ userId, success: true });
          } catch (error) {
            results.push({ userId, success: false });
          }
        }
      } else {
        throw new Error('Selecione pelo menos um destinatário');
      }

      setMessage({ type: 'success', text: 'Notificação enviada com sucesso!' });
      setForm({ title: '', body: '', url: '', userIds: [], sendToAll: true });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erro ao enviar notificação'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Notificações Push</h1>
        <p className="text-gray-600">
          Envie notificações push para usuários específicos ou para todos os usuários
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Título da Notificação *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={form.title}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              placeholder="Ex: Serviço Configurado"
            />
          </div>

          <div>
            <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-2">
              Mensagem *
            </label>
            <textarea
              id="body"
              name="body"
              value={form.body}
              onChange={handleInputChange}
              required
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              placeholder="Ex: Seu serviço de VoIP foi configurado com sucesso!"
            />
          </div>

          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-2">
              URL de Destino (opcional)
            </label>
            <select
              id="url"
              name="url"
              value={form.url}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            >
              <option value="">Página inicial (/) - padrão</option>
              <option value="/dashboard">Dashboard do usuário</option>
              <option value="/services">Lista de serviços</option>
              <option value="/profile">Perfil do usuário</option>
              <option value="/invoices">Faturas e pagamentos</option>
              <option value="/call-reports">Relatórios de chamadas</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              Selecione para onde o usuário será direcionado ao clicar na notificação
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Destinatários
            </label>

            {/* Toggle para escolher entre todos ou específicos */}
            <div className="flex items-center space-x-4 mb-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="recipientType"
                  checked={form.sendToAll}
                  onChange={() => setForm(prev => ({ ...prev, sendToAll: true, userIds: [] }))}
                  className="mr-2 text-cyan-500 focus:ring-cyan-500"
                />
                <span className="text-sm text-gray-700">Todos os usuários</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="recipientType"
                  checked={!form.sendToAll}
                  onChange={() => setForm(prev => ({ ...prev, sendToAll: false }))}
                  className="mr-2 text-cyan-500 focus:ring-cyan-500"
                />
                <span className="text-sm text-gray-700">Selecionar usuários específicos</span>
              </label>
            </div>

            {/* Seção de seleção específica de usuários */}
            {!form.sendToAll && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                {/* Usuários selecionados - Tags */}
                {form.userIds.length > 0 && (
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-2">
                      {form.userIds.map(userId => {
                        const user = users.find(u => u.id.toString() === userId);
                        return (
                          <span
                            key={userId}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                          >
                            {user ? user.name : `Usuário ${userId}`}
                            <button
                              type="button"
                              onClick={() => setForm(prev => ({
                                ...prev,
                                userIds: prev.userIds.filter(id => id !== userId)
                              }))}
                              className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-cyan-400 hover:bg-cyan-200 hover:text-cyan-500"
                            >
                              <span className="text-xs">×</span>
                            </button>
                          </span>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {form.userIds.length} usuário{form.userIds.length !== 1 ? 's' : ''} selecionado{form.userIds.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                )}

                {/* Lista de usuários com checkboxes */}
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                  {loadingUsers ? (
                    <div className="p-4 text-center text-gray-500">
                      Carregando usuários...
                    </div>
                  ) : users.filter(user => user.is_active).length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      Nenhum usuário ativo encontrado
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {users
                        .filter(user => user.is_active)
                        .map(user => (
                          <label
                            key={user.id}
                            className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={form.userIds.includes(user.id.toString())}
                              onChange={(e) => {
                                const userId = user.id.toString();
                                if (e.target.checked) {
                                  setForm(prev => ({
                                    ...prev,
                                    userIds: [...prev.userIds, userId]
                                  }));
                                } else {
                                  setForm(prev => ({
                                    ...prev,
                                    userIds: prev.userIds.filter(id => id !== userId)
                                  }));
                                }
                              }}
                              className="mr-3 text-cyan-500 focus:ring-cyan-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{user.name}</div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </label>
                        ))}
                    </div>
                  )}
                </div>

                {/* Botões de ação rápida */}
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({
                      ...prev,
                      userIds: users.filter(u => u.is_active).map(u => u.id.toString())
                    }))}
                    className="text-xs text-cyan-600 hover:text-cyan-800"
                  >
                    Selecionar todos
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, userIds: [] }))}
                    className="text-xs text-gray-600 hover:text-gray-800"
                  >
                    Limpar seleção
                  </button>
                </div>
              </div>
            )}

            {/* Resumo dos destinatários */}
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
              {form.sendToAll ? (
                <span>📢 Notificação será enviada para <strong>todos os usuários ativos</strong></span>
              ) : form.userIds.length === 0 ? (
                <span>⚠️ Selecione pelo menos um usuário</span>
              ) : (
                <span>🎯 Notificação será enviada para <strong>{form.userIds.length} usuário{form.userIds.length !== 1 ? 's' : ''}</strong></span>
              )}
            </div>
          </div>

          {message && (
            <div className={`p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {message.text}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Enviando...' : 'Enviar Notificação'}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-8 space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-2">💡 Dicas para Notificações Eficazes</h3>
          <ul className="text-blue-800 space-y-1 text-sm">
            <li>• <strong>Título curto e claro:</strong> Use títulos que chamem atenção e sejam diretos</li>
            <li>• <strong>Mensagem objetiva:</strong> Seja específico sobre o que o usuário precisa saber</li>
            <li>• <strong>URL apropriada:</strong> Direcione para a página mais relevante para a ação necessária</li>
            <li>• <strong>Timing:</strong> Considere o horário dos usuários ao enviar notificações</li>
          </ul>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-green-900 mb-2">📱 URLs Recomendadas por Situação</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-green-800 mb-1">Para Serviços:</h4>
              <ul className="text-green-700 space-y-1">
                <li>• <strong>/services</strong> - Novo serviço configurado</li>
                <li>• <strong>/dashboard</strong> - Status de serviço alterado</li>
                <li>• <strong>/services</strong> - Problema em serviço</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-green-800 mb-1">Para Financeiro:</h4>
              <ul className="text-green-700 space-y-1">
                <li>• <strong>/invoices</strong> - Nova fatura gerada</li>
                <li>• <strong>/invoices</strong> - Pagamento confirmado</li>
                <li>• <strong>/profile</strong> - Dados de cobrança atualizados</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-yellow-900 mb-2">⚠️ Requisitos Técnicos</h3>
          <ul className="text-yellow-800 space-y-1 text-sm">
            <li>• As notificações só funcionam em dispositivos com <strong>HTTPS</strong></li>
            <li>• Os usuários precisam ter <strong>permitido notificações</strong> no navegador</li>
            <li>• As notificações aparecem mesmo quando o app não está aberto</li>
            <li>• Clique na notificação redireciona para a URL especificada</li>
            <li>• Funciona em navegadores modernos: Chrome, Firefox, Edge, Safari</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationsPage;